/**
 * Icon Component Definition
 *
 * An icon component supporting various icon libraries and styling.
 */

export const iconComponent = {
  type: 'icon',
  label: 'Icon',
  icon: 'Star',
  description: 'Icon with size and color options',
  category: 'content',

  canHaveChildren: false,

  resolveStyles(props) {
    return {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: props.alignment === 'left' ? 'flex-start' :
                      props.alignment === 'right' ? 'flex-end' : 'center',
    };
  },

  defaultProps: {
    // Content
    icon: 'Star',
    iconType: 'lucide', // 'lucide', 'emoji', 'custom'
    customIcon: '',

    // Size
    size: 24,
    width: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    height: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    alignment: 'center',

    // Colors
    color: '#4f46e5',
    backgroundColor: 'transparent',

    // Spacing
    padding: { base: { top: 8, bottom: 8, left: 8, right: 8, unit: 'px' }, tablet: null, desktop: null },
    margin: { base: { top: 0, bottom: 0, left: 0, right: 0, unit: 'px' }, tablet: null, desktop: null },

    // Border
    borderWidth: 0,
    borderStyle: 'solid',
    borderColor: 'transparent',
    borderRadius: 0,

    // Positioning
    position: 'static',
    top: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    right: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    bottom: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    left: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    zIndex: 'auto',

    // Custom CSS
    customCSS: '',

    // Flex child (when inside a flex parent) — responsive
    flexChild: { base: { mode: 'auto', basis: 50 }, tablet: null, desktop: null },
  },

  settingsSchema: [
    // Content category
    {
      key: 'iconType',
      type: 'select',
      label: 'Icon Type',
      category: 'Content',
      default: 'lucide',
      options: [
        { value: 'lucide', label: 'Lucide Icons' },
        { value: 'emoji', label: 'Emoji' },
        { value: 'custom', label: 'Custom (URL or SVG)' },
      ],
    },
    {
      key: 'icon',
      type: 'text',
      label: 'Icon Name',
      category: 'Content',
      default: 'Star',
      placeholder: 'Star, Heart, Check, etc.',
      showWhen: { field: 'iconType', equals: 'lucide' },
    },
    {
      key: 'icon',
      type: 'text',
      label: 'Emoji',
      category: 'Content',
      placeholder: 'Enter an emoji',
      showWhen: { field: 'iconType', equals: 'emoji' },
    },
    {
      key: 'customIcon',
      type: 'textarea',
      label: 'Custom Icon (SVG or URL)',
      category: 'Content',
      rows: 3,
      showWhen: { field: 'iconType', equals: 'custom' },
    },

    // Size category
    {
      key: 'size',
      type: 'number',
      label: 'Size (px)',
      category: 'Size',
      min: 12,
      max: 200,
      default: 24,
      showWhen: { field: 'iconType', notEquals: 'custom' },
    },
    {
      key: 'width',
      type: 'responsiveDimension',
      label: 'Width',
      category: 'Size',
      showWhen: { field: 'iconType', equals: 'custom' },
    },
    {
      key: 'height',
      type: 'responsiveDimension',
      label: 'Height',
      category: 'Size',
      showWhen: { field: 'iconType', equals: 'custom' },
    },
    {
      key: 'alignment',
      type: 'select',
      label: 'Alignment',
      category: 'Size',
      default: 'center',
      options: [
        { value: 'left', label: 'Left' },
        { value: 'center', label: 'Center' },
        { value: 'right', label: 'Right' },
      ],
    },

    // Colors category
    {
      key: 'color',
      type: 'color',
      label: 'Icon Color',
      category: 'Colors',
      default: '#4f46e5',
    },
    {
      key: 'backgroundColor',
      type: 'color',
      label: 'Background Color',
      category: 'Colors',
      default: 'transparent',
    },

    // Spacing category
    {
      key: 'padding',
      type: 'responsivePadding',
      label: 'Padding',
      category: 'Spacing',
    },
    {
      key: 'margin',
      type: 'responsiveMargin',
      label: 'Margin',
      category: 'Spacing',
    },

    // Border category
    {
      key: 'borderWidth',
      type: 'number',
      label: 'Border Width (px)',
      category: 'Border',
      min: 0,
      max: 10,
      default: 0,
    },
    {
      key: 'borderStyle',
      type: 'select',
      label: 'Border Style',
      category: 'Border',
      default: 'solid',
      options: [
        { value: 'none', label: 'None' },
        { value: 'solid', label: 'Solid' },
        { value: 'dashed', label: 'Dashed' },
        { value: 'dotted', label: 'Dotted' },
      ],
    },
    {
      key: 'borderColor',
      type: 'color',
      label: 'Border Color',
      category: 'Border',
      default: 'transparent',
    },
    {
      key: 'borderRadius',
      type: 'number',
      label: 'Border Radius (px)',
      category: 'Border',
      min: 0,
      max: 100,
      default: 0,
    },

    // Position category
    {
      key: 'position',
      type: 'select',
      label: 'Position',
      category: 'Position',
      default: 'static',
      options: [
        { value: 'static', label: 'Static (Normal)' },
        { value: 'relative', label: 'Relative' },
        { value: 'absolute', label: 'Absolute' },
        { value: 'fixed', label: 'Fixed' },
        { value: 'sticky', label: 'Sticky' },
      ],
    },
    {
      key: 'top',
      type: 'responsiveDimension',
      label: 'Top',
      category: 'Position',
      showWhen: { field: 'position', notEquals: 'static' },
    },
    {
      key: 'right',
      type: 'responsiveDimension',
      label: 'Right',
      category: 'Position',
      showWhen: { field: 'position', notEquals: 'static' },
    },
    {
      key: 'bottom',
      type: 'responsiveDimension',
      label: 'Bottom',
      category: 'Position',
      showWhen: { field: 'position', notEquals: 'static' },
    },
    {
      key: 'left',
      type: 'responsiveDimension',
      label: 'Left',
      category: 'Position',
      showWhen: { field: 'position', notEquals: 'static' },
    },
    {
      key: 'zIndex',
      type: 'number',
      label: 'Z-Index',
      category: 'Position',
      placeholder: 'auto',
      showWhen: { field: 'position', notEquals: 'static' },
    },

    // Advanced category
    {
      key: 'customCSS',
      type: 'customCSS',
      label: 'Custom CSS',
      category: 'Advanced',
      default: '',
    },

    // Flex Child sizing
    {
      key: 'flexChild',
      type: 'flexChild',
      label: 'Flex Child',
      category: 'Layout',
      showWhenParent: { field: 'display', in: ['flex', 'inline-flex'] },
      description: 'Controls sizing when inside a flex container',
      default: { base: { mode: 'auto', basis: 50 }, tablet: null, desktop: null },
    },
  ],

  // Render function - will be implemented in the unified renderer
  render: null,
};

export default iconComponent;
